import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  getHostProperties, 
  publishProperty, 
  pauseProperty,
  submitPropertyForReview 
} from '../../lib/api';
import { getListingUrl } from '../../lib/seoUtils';
import { toast } from 'sonner';
import {
  Plus,
  Building,
  Edit,
  Eye,
  EyeOff,
  ExternalLink,
  ArrowRight,
  Loader2,
  Bed,
  Bath,
  Users,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function HostProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectionDialog, setRejectionDialog] = useState({ open: false, property: null });

  const loadProperties = async (cursor = null) => {
    try {
      const response = await getHostProperties({ limit: 20, cursor });
      
      if (cursor) {
        setProperties(prev => [...prev, ...response.data.items]);
      } else {
        setProperties(response.data.items || []);
      }
      
      setNextCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
  }, []);

  const handleSubmitForReview = async (propertyId) => {
    setActionLoading(propertyId);
    try {
      await submitPropertyForReview(propertyId);
      setProperties(prev => 
        prev.map(p => p.id === propertyId ? { ...p, status: 'pending_review', rejection_reason: null } : p)
      );
      toast.success('Property submitted for review! You will be notified once approved.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for review');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePublish = async (propertyId) => {
    setActionLoading(propertyId);
    try {
      const response = await publishProperty(propertyId);
      const newStatus = response.data.status;
      setProperties(prev => 
        prev.map(p => p.id === propertyId ? { ...p, status: newStatus } : p)
      );
      if (newStatus === 'pending_review') {
        toast.success('Property submitted for review! You will be notified once approved.');
      } else {
        toast.success('Property is now live!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish property');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async (propertyId) => {
    setActionLoading(propertyId);
    try {
      await pauseProperty(propertyId);
      setProperties(prev => 
        prev.map(p => p.id === propertyId ? { ...p, status: 'paused' } : p)
      );
      toast.success('Property paused');
    } catch (error) {
      toast.error('Failed to pause property');
    } finally {
      setActionLoading(null);
    }
  };

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      draft: {
        label: 'Draft',
        className: 'bg-slate-100 text-slate-700',
        icon: Edit,
        description: 'Not yet submitted'
      },
      pending_review: {
        label: 'Pending Review',
        className: 'bg-amber-100 text-amber-700',
        icon: Clock,
        description: 'Awaiting admin approval'
      },
      approved: {
        label: 'Approved',
        className: 'bg-green-100 text-green-700',
        icon: CheckCircle,
        description: 'Live and visible to guests'
      },
      rejected: {
        label: 'Rejected',
        className: 'bg-red-100 text-red-700',
        icon: XCircle,
        description: 'Review feedback available'
      },
      paused: {
        label: 'Paused',
        className: 'bg-slate-200 text-slate-600',
        icon: EyeOff,
        description: 'Hidden from search'
      },
      suspended: {
        label: 'Suspended',
        className: 'bg-red-200 text-red-800',
        icon: AlertCircle,
        description: 'Suspended by admin'
      },
      // Legacy status - treat as approved
      published: {
        label: 'Live',
        className: 'bg-green-100 text-green-700',
        icon: CheckCircle,
        description: 'Live and visible to guests'
      }
    };
    return configs[status] || configs.draft;
  };

  const getImageUrl = (property) => {
    if (property.cover_image) {
      return property.cover_image.startsWith('http') 
        ? property.cover_image 
        : `${API_URL}${property.cover_image}`;
    }
    return 'https://images.unsplash.com/photo-1753194931548-065891743746?w=400&h=300&fit=crop';
  };

  const canSubmitForReview = (status) => ['draft', 'rejected'].includes(status);
  const canPause = (status) => ['approved', 'published'].includes(status);
  const canRepublish = (status) => status === 'paused';
  const canViewPublic = (status) => ['approved', 'published'].includes(status);

  return (
    <div className="space-y-6" data-testid="host-properties">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">My Properties</h1>
          <p className="text-slate-500 mt-1">Manage your vacation rental listings</p>
        </div>
        <Link to="/app/host/properties/new">
          <Button className="btn-primary" data-testid="add-property-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Property
          </Button>
        </Link>
      </div>

      {/* Status Legend */}
      <Card className="bg-slate-50">
        <CardContent className="p-4">
          <p className="text-sm font-medium text-slate-700 mb-2">Property Status Guide:</p>
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-slate-400"></span>
              Draft - Not submitted
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              Pending - Under review
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              Approved - Visible to guests
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-400"></span>
              Rejected - Needs changes
            </span>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : properties.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {properties.map((property) => {
              const statusConfig = getStatusConfig(property.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card key={property.id} className="overflow-hidden" data-testid={`property-card-${property.id}`}>
                  <div className="flex">
                    {/* Image */}
                    <div className="w-40 h-48 flex-shrink-0 relative">
                      <img 
                        src={getImageUrl(property)}
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                      {/* Status overlay for pending */}
                      {property.status === 'pending_review' && (
                        <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                          <Clock className="w-8 h-8 text-amber-600" />
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <CardContent className="flex-1 p-4 flex flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <Badge className={`${statusConfig.className} gap-1`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </Badge>
                          <h3 className="font-medium text-slate-900 mt-2 line-clamp-1">
                            {property.title}
                          </h3>
                          <p className="text-sm text-slate-500">{property.city_id}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mt-2 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Bed className="w-4 h-4" />
                          {property.beds}
                        </span>
                        <span className="flex items-center gap-1">
                          <Bath className="w-4 h-4" />
                          {property.baths}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {property.max_guests}
                        </span>
                      </div>

                      <p className="text-lg font-semibold text-slate-900 mt-2">
                        {formatPrice(property.base_price_nightly)}/night
                      </p>

                      {/* Rejection Reason Alert */}
                      {property.status === 'rejected' && property.rejection_reason && (
                        <div 
                          className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 cursor-pointer hover:bg-red-100 transition-colors"
                          onClick={() => setRejectionDialog({ open: true, property })}
                          data-testid={`rejection-alert-${property.id}`}
                        >
                          <div className="flex items-center gap-1 font-medium">
                            <XCircle className="w-3 h-3" />
                            Review Feedback
                          </div>
                          <p className="mt-1 line-clamp-2">{property.rejection_reason}</p>
                          <span className="text-red-600 underline mt-1 inline-block">Click to read full feedback</span>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 mt-auto pt-3 flex-wrap">
                        <Link to={`/app/host/properties/${property.id}`}>
                          <Button variant="outline" size="sm" data-testid={`edit-btn-${property.id}`}>
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                        </Link>
                        
                        {canViewPublic(property.status) && (
                          <a 
                            href={getListingUrl(property)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            data-testid={`view-btn-${property.id}`}
                          >
                            <Button variant="outline" size="sm">
                              <ExternalLink className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </a>
                        )}
                        
                        {canSubmitForReview(property.status) && (
                          <Button 
                            size="sm" 
                            className="btn-primary"
                            onClick={() => handleSubmitForReview(property.id)}
                            disabled={actionLoading === property.id}
                            data-testid={`submit-review-btn-${property.id}`}
                          >
                            {actionLoading === property.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-1" />
                                Submit for Review
                              </>
                            )}
                          </Button>
                        )}
                        
                        {canPause(property.status) && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handlePause(property.id)}
                            disabled={actionLoading === property.id}
                            data-testid={`pause-btn-${property.id}`}
                          >
                            {actionLoading === property.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <EyeOff className="w-4 h-4 mr-1" />
                                Pause
                              </>
                            )}
                          </Button>
                        )}
                        
                        {canRepublish(property.status) && (
                          <Button 
                            size="sm" 
                            className="btn-primary"
                            onClick={() => handlePublish(property.id)}
                            disabled={actionLoading === property.id}
                            data-testid={`republish-btn-${property.id}`}
                          >
                            {actionLoading === property.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-1" />
                                Re-publish
                              </>
                            )}
                          </Button>
                        )}

                        {property.status === 'pending_review' && (
                          <span className="text-xs text-amber-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Under review
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>

          {hasMore && (
            <div className="text-center">
              <Button 
                variant="outline"
                onClick={() => loadProperties(nextCursor)}
              >
                Load More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
              No properties yet
            </h3>
            <p className="text-slate-500 mb-6">
              Create your first listing to start hosting guests.
            </p>
            <Link to="/app/host/properties/new">
              <Button className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Property
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Rejection Reason Dialog */}
      <Dialog open={rejectionDialog.open} onOpenChange={(open) => setRejectionDialog({ open, property: null })}>
        <DialogContent className="max-w-md" data-testid="rejection-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              Property Review Feedback
            </DialogTitle>
            <DialogDescription>
              Your property "{rejectionDialog.property?.title}" requires changes before approval.
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-medium text-red-800 mb-2">Admin Feedback:</p>
              <p className="text-red-700 text-sm whitespace-pre-wrap">
                {rejectionDialog.property?.rejection_reason}
              </p>
            </div>
            
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Next steps:</strong>
              </p>
              <ol className="text-sm text-slate-600 mt-2 list-decimal list-inside space-y-1">
                <li>Review the feedback above</li>
                <li>Edit your property to address the issues</li>
                <li>Click "Submit for Review" to resubmit</li>
              </ol>
            </div>
            
            <div className="mt-4 flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setRejectionDialog({ open: false, property: null })}
              >
                Close
              </Button>
              <Link to={`/app/host/properties/${rejectionDialog.property?.id}`}>
                <Button className="btn-primary">
                  <Edit className="w-4 h-4 mr-1" />
                  Edit Property
                </Button>
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
